import React, { useState } from 'react';
import { Plus, Mail, Trash2, X, Upload, Users, ShieldCheck, Sparkles, Star, Image as ImageIcon } from 'lucide-react';
import { Staff, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface ShopTeamProps {
  staff: Staff[];
  shopId: string;
  onAddStaff: (newStaff: Staff) => void;
  onRemoveStaff: (staffId: string) => void;
}

export const ShopTeam: React.FC<ShopTeamProps> = ({ staff, shopId, onAddStaff, onRemoveStaff }) => {
  const { language } = useTranslation();
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('Technician');
  const [newStaffSkills, setNewStaffSkills] = useState('');
  const [newStaffImage, setNewStaffImage] = useState<string | null>(null);

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Team Management', KH: 'ការគ្រប់គ្រងក្រុមការងារ', ZH: '团队管理', JA: 'チーム管理', KO: '팀 관리' },
    sub: { EN: 'Manage your staff, assign roles, and track performance.', KH: 'គ្រប់គ្រងបុគ្សិក កំណត់តួនាទី និងតាមដានប្រសិទ្ធភាពការងារ។', ZH: '管理您的员工，分配角色并跟踪绩效。', JA: 'スタッフの管理、役割の割り当て、パフォーマンスの追跡を行います。', KO: '직원을 관리하고, 역할을 배정하며, 성과를 추적하십시오.' },
    add_staff: { EN: 'Add Staff', KH: 'បន្ថែមបុគ្គលិក', ZH: '添加员工', JA: 'スタッフ追加', KO: '직원 추가' },
    contact: { EN: 'Contact', KH: 'ទំនាក់ទំនង', ZH: '联系', JA: '連絡先', KO: '연락처' },
    delete: { EN: 'Remove', KH: 'លុបចេញ', ZH: '删除', JA: '削除', KO: '삭제' },
    new_member: { EN: 'New Team Member', KH: 'សមាជិកក្រុមថ្មី', ZH: '新团队成员', JA: '新規チームメンバー', KO: '새 팀원' },
    info: { EN: 'Manage team access and visibility.', KH: 'គ្រប់គ្រងសិទ្ធិចូលប្រើប្រាស់ និងការបង្ហាញព័ត៌មានក្រុម។', ZH: '管理团队访问权限和可见性。', JA: 'チームのアクセスと可視性を管理します。', KO: '팀 액세스 및 가시성을 관리하십시오.' },
    full_name: { EN: 'Full Name', KH: 'ឈ្មោះពេញ', ZH: '姓名', JA: '氏名', KO: '성명' },
    email: { EN: 'Email Address', KH: 'អាសយដ្ឋានអ៊ីមែល', ZH: '电子邮件地址', JA: 'メールアドレス', KO: 'អ៊ីមែល' },
    role: { EN: 'Role', KH: 'តួនាទី', ZH: '角色', JA: '役割', KO: '역할' },
    skills: { EN: 'Skills (comma separated)', KH: 'ជំនាញ (បំបែកដោយក្បៀស)', ZH: '技能 (用逗号分隔)', JA: 'スキル (カンマ区切り)', KO: '기술 (콤마로 구분)' },
    upload: { EN: 'Upload Photo', KH: 'ផ្ទុកឡើងរូបថត', ZH: '上传照片', JA: '写真をアップロード', KO: '사진 업로드' },
    add: { EN: 'Add Member', KH: 'បន្ថែមសមាជិក', ZH: '添加成员', JA: 'メンバーを追加', KO: '멤버 추가' },
    technician: { EN: 'Technician', KH: 'ជាងបច្ចេកទេស', ZH: '技师', JA: '技術者', KO: '技術者' },
    manager: { EN: 'Manager', KH: 'អ្នកគ្រប់គ្រង', ZH: '经理', JA: 'マネージャー', KO: '매니저' },
    support: { EN: 'Support', KH: 'ផ្នែកគាំទ្រ', ZH: '支持人员', JA: 'サポート', KO: '지원' },
    empty: { EN: 'No team members yet.', KH: 'មិនទាន់មានសមាជិកក្រុមនៅឡើយទេ។', ZH: '目前还没有团队成员。', JA: 'チームメンバーがまだいません。', KO: '아직 팀원이 없습니다.' }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewStaffImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddStaffSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaffName || !newStaffEmail || !newStaffImage) return;

    const newStaff: Staff = {
      id: `st${Date.now()}`,
      name: newStaffName,
      email: newStaffEmail,
      role: newStaffRole,
      skills: newStaffSkills.split(',').map(s => s.trim()).filter(s => s.length > 0),
      shopId: shopId,
      avatarUrl: newStaffImage
    };

    onAddStaff(newStaff);
    setIsAddingStaff(false);
    setNewStaffName('');
    setNewStaffEmail('');
    setNewStaffRole('Technician');
    setNewStaffSkills('');
    setNewStaffImage(null);
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-gradient-to-tr from-violet-600 to-indigo-700 rounded-[3rem] p-10 text-white shadow-2xl shadow-violet-200 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[100px] -mr-32 -mt-32 group-hover:bg-white/20 transition-all duration-1000"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-2xl mb-4 border border-white/10">
               <ShieldCheck size={16} />
               <span className="text-[10px] font-black uppercase tracking-widest">Human Capital</span>
            </div>
            <h2 className="text-3xl font-black mb-3 tracking-tighter">{texts.title[language]}</h2>
            <p className="text-violet-100 font-bold max-w-md text-lg opacity-80">{texts.sub[language]}</p>
          </div>
          <button 
            onClick={() => setIsAddingStaff(true)}
            className="bg-white text-violet-600 px-10 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
          >
            <Plus size={20} strokeWidth={3} /> {texts.add_staff[language]}
          </button>
        </div>
      </div>

      {staff.length === 0 ? (
        <div className="py-32 bg-white rounded-[3rem] border-2 border-dashed border-slate-100 flex flex-col items-center justify-center opacity-30 text-center">
           <Users size={64} className="mb-6" />
           <p className="text-2xl font-black tracking-tight text-slate-400">{texts.empty[language]}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {staff.map(s => (
            <div key={s.id} className="bg-white rounded-[3rem] p-8 border border-slate-100 shadow-sm flex flex-col items-center text-center relative group hover:shadow-xl transition-all duration-500">
              <div className="w-28 h-28 rounded-[2.5rem] bg-slate-50 mb-6 overflow-hidden shadow-inner border-4 border-white group-hover:scale-105 transition-transform duration-500">
                <img src={s.avatarUrl} alt={s.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">{s.name}</h3>
              <p className="text-[10px] font-black text-violet-600 uppercase tracking-widest mb-6">{s.role}</p>
              
              <div className="flex flex-wrap gap-2 justify-center mb-8">
                {s.skills.slice(0, 3).map((skill, i) => (
                  <span key={i} className="text-[9px] font-black uppercase tracking-widest bg-slate-50 text-slate-400 px-3 py-1 rounded-lg border border-slate-100">
                    {skill}
                  </span>
                ))}
                {s.skills.length > 3 && <span className="text-[9px] font-black text-slate-300">+{s.skills.length - 3}</span>}
              </div>

              <div className="flex gap-3 w-full mt-auto">
                <button className="flex-1 py-4 rounded-2xl bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest hover:bg-violet-600 transition-all shadow-lg flex items-center justify-center gap-2">
                  <Mail size={14} /> {texts.contact[language]}
                </button>
                <button 
                  onClick={() => onRemoveStaff(s.id)}
                  className="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-all"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAddingStaff && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <div className="bg-white rounded-[3.5rem] w-full max-w-lg shadow-2xl animate-in zoom-in duration-300 relative overflow-hidden border border-white/20">
              <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white sticky top-0 z-10">
                <div>
                   <h3 className="text-2xl font-black text-slate-900 tracking-tighter">{texts.new_member[language]}</h3>
                   <p className="text-slate-400 text-sm font-bold">{texts.info[language]}</p>
                </div>
                <button onClick={() => setIsAddingStaff(false)} className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-all">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleAddStaffSubmit} className="p-10 space-y-8 max-h-[75vh] overflow-y-auto no-scrollbar">
                <div className="flex justify-center mb-4">
                    <div className="relative group cursor-pointer">
                      <div className="w-32 h-32 rounded-[2.5rem] bg-slate-50 overflow-hidden border-2 border-dashed border-slate-200 flex flex-col items-center justify-center group-hover:bg-slate-100 transition-all">
                          {newStaffImage ? (
                            <img src={newStaffImage} className="w-full h-full object-cover" />
                          ) : (
                            <>
                               <ImageIcon size={32} className="text-slate-200 mb-2" />
                               <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest">
                                 {texts.upload[language]} <span className="text-rose-500 ml-0.5">*</span>
                               </span>
                            </>
                          )}
                      </div>
                      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={handleImageUpload} required />
                    </div>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        {texts.full_name[language]} <span className="text-rose-500 ml-0.5">*</span>
                      </label>
                      <input 
                        type="text" value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} required
                        className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all placeholder:text-slate-300 shadow-inner"
                      />
                  </div>
                  <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        {texts.email[language]} <span className="text-rose-500 ml-0.5">*</span>
                      </label>
                      <input 
                        type="email" value={newStaffEmail} onChange={(e) => setNewStaffEmail(e.target.value)} required
                        className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all placeholder:text-slate-300 shadow-inner"
                      />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          {texts.role[language]} <span className="text-rose-500 ml-0.5">*</span>
                        </label>
                        <select 
                          value={newStaffRole} onChange={(e) => setNewStaffRole(e.target.value)}
                          className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all appearance-none cursor-pointer shadow-inner uppercase text-[10px] tracking-widest"
                        >
                          <option value="Technician">{texts.technician[language]}</option>
                          <option value="Manager">{texts.manager[language]}</option>
                          <option value="Support">{texts.support[language]}</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.skills[language]}</label>
                        <input 
                          type="text" value={newStaffSkills} onChange={(e) => setNewStaffSkills(e.target.value)}
                          className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all placeholder:text-slate-300 shadow-inner"
                          placeholder="Repair, Sales..."
                        />
                      </div>
                  </div>
                </div>
                
                <div className="pt-6">
                   <button 
                     type="submit" 
                     className="w-full py-6 bg-slate-900 text-white rounded-[2rem] font-black text-lg hover:bg-violet-600 shadow-2xl shadow-slate-900/20 transition-all transform active:scale-95 uppercase tracking-widest flex items-center justify-center gap-3"
                   >
                     <Sparkles size={20} fill="currentColor" /> {texts.add[language]}
                   </button>
                </div>
              </form>
            </div>
          </div>
      )}
    </div>
  );
};